import { action, computed, configure, makeObservable, observable } from 'mobx';
import { toast } from 'react-hot-toast';
import { AccountStatusEnum } from '../../constants/enums';
import userTypeEnum from '../../constants/enums/user-type-enum';
import URLConstants from '../../constants/url-constants';
import {
  getUserIdFromToken,
  getUserType,
} from '../../helpers/localstorage-helper';
import {
  IAddUser,
  ISendMail,
  IUpdatePassword,
  IUpdateUser,
} from '../../models/forms/IAddUpdateUser';
import {
  IChangePassword,
  IChangePasswordNew,
} from '../../models/forms/IChangePassword';
import { IMultiSelectOption } from '../../models/ICommon';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IUserList,
  IUserListVM,
  IUserVM,
} from '../../models/response/IUserResponse';
import { IUserState } from '../../models/state/IUserState';
import { formatMessage } from '../../translations/format-message';
import {
  AddEditUserInitialState,
  GetAllUserState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';

configure({
  enforceActions: 'never',
});

export class UserStore implements IUserState {
  tenantAccessId = getUserIdFromToken();
  initialStateValue = ObservableInitialState;
  userList: IUserListVM = GetAllUserState;
  usersListByTenantId: IUserListVM = GetAllUserState;
  userListAll: any = [];
  user: any | undefined = undefined;
  userState = { ...this.initialStateValue };
  activateUserAccountState = { ...this.initialStateValue };
  addUpdateUserState = { ...this.initialStateValue };
  changePasswordState = { ...this.initialStateValue };
  deleteUserState = { ...this.initialStateValue };
  resendActivationLinkState = { ...this.initialStateValue };
  getUserListServiceState = { ...this.initialStateValue };
  getUsersByTenantIdServiceState = { ...this.initialStateValue };
  getAllUsersServiceState = { ...this.initialStateValue };
  activeInactiveState = { ...this.initialStateValue };
  changePasswordStateNew = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      GetUserListService: action,
      getUserListServiceState: observable,
      userList: observable,
      allUsers: computed,
      UpdateUserService: action,
      addUpdateUserState: observable,
      DeleteUserService: action,
      deleteUserState: observable,
      user: observable,
      userState: observable,
      GetUserByIdService: action,
      changePasswordState: observable,
      activateUserAccountState: observable,
      resendActivationLinkState: observable,
      AddUserService: action,
      ActivateUserService: action,
      ResendAccountActivateLinkService: action,
      resetAddUpdateUserState: action,
      resetAccountActivationLink: action,
      reset: action,
      resetUserDetail: action,
      resetDeleteUser: action,
      resetChangePassword: action,
      resetAccountActivationState: action,
      userDetails: computed,
      UpdateUserPasswordService: action,
      GetUsersByTenantIdService: action,
      getUsersByTenantIdServiceState: observable,
      usersListByTenantId: observable,
      GetAllUsersService: action,
      getAllUsersServiceState: observable,
      userListAll: observable,
      UpdateUserAccountStatusService: action,
      activeInactiveState: observable,
      resetGetUserListServiceState: action,
      resetActiveInactiveState: action,
      changePasswordStateNew: observable,
      allUserAssignedMulitSelectOptions: computed,
    });
  }

  //#region Actions
  GetUserListService = (
    currentPage: number,
    pagerSize: number,
    tenantId: number,
    filterQuery?: string
  ) => {
    this.getUserListServiceState.inProgress = true;

    const url = `${URLConstants.GetAllUsersList}?TenantId=${tenantId}&PageSize=${pagerSize}&PageNo=${currentPage}&filterQuery=${filterQuery}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IUserListVM>>) => {
        if (response.data.Error) {
          this.getUserListServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.getUserListServiceState.success = true;
          this.userList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getUserListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getUserListServiceState.inProgress = false;
        })
      );
  };

  UpdateUserService = (id: number, data: IUpdateUser) => {
    this.addUpdateUserState.inProgress = true;
    let url = URLConstants.UpdateUser;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateUserState.error = response.data.Message;
        } else this.addUpdateUserState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateUserState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateUserState.inProgress = false;
        })
      );
  };

  DeleteUserService = (id: number) => {
    this.deleteUserState.inProgress = true;
    let url = URLConstants.DeleteUser + '/' + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteUserState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteUserState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteUserState.error = err;
      })
      .finally(
        action(() => {
          this.deleteUserState.inProgress = false;
        })
      );
  };

  AddUserService = (data: IAddUser) => {
    this.addUpdateUserState.inProgress = true;
    let url = URLConstants.AddUser;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateUserState.error = response.data.Message;
        } else this.addUpdateUserState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateUserState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateUserState.inProgress = false;
        })
      );
  };

  ResendAccountActivateLinkService = (data: ISendMail) => {
    this.resendActivationLinkState.inProgress = true;
    let url = URLConstants.ResendLink;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.resendActivationLinkState.error = response.data.Message;
        } else this.resendActivationLinkState.success = true;
      })
      .catch((err: string) => {
        this.resendActivationLinkState.error = err;
      })
      .finally(
        action(() => {
          this.resendActivationLinkState.inProgress = false;
        })
      );
  };

  ActivateUserService = (
    userId: number,
    email: string,
    accountStatus: number
  ) => {
    this.activateUserAccountState.inProgress = true;
    let url = URLConstants.ActivateAccount;
    let obj = {
      UserId: userId,
      Email: email,
      AccountStatus: accountStatus,
    };
    return baseService
      .postRequest(url, obj)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activateUserAccountState.error = response.data.Message;
        } else {
          this.activateUserAccountState.success = true;
          this.activateUserAccountState.error = response.data.Message;
        }
      })
      .catch((err: string) => {
        this.activateUserAccountState.error = err;
      })
      .finally(
        action(() => {
          this.activateUserAccountState.inProgress = false;
        })
      );
  };

  GetUserByIdService = (id: number) => {
    this.userState.inProgress = true;
    let url = `${URLConstants.GetUserById}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IUserVM>>) => {
        if (response.data.Error) {
          this.userState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.user = data;
        }
      })
      .catch((err: string) => {
        this.userState.error = err;
      })
      .finally(
        action(() => {
          this.userState.inProgress = false;
        })
      );
  };

  UpdateUserPasswordService = (
    oldPassword: string,
    newPassword: string,
    tenantUserId: number
  ) => {
    this.changePasswordState.inProgress = true;
    let data: IUpdatePassword = {
      OldPassword: oldPassword,
      NewPassword: newPassword,
      TenantUserId: tenantUserId,
    };
    const url = URLConstants.UpdateUserPassword;
    return baseService
      .putRequest(url, data)
      .then((response: any) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.changePasswordState.success = true;
          toast.success(formatMessage(response.data.Message));
        }
      })
      .catch((err: string) => {
        this.changePasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  UpdatePasswordService = (data: IChangePasswordNew) => {
    this.changePasswordState.inProgress = true;
    let url: string = URLConstants.UpdateUserPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.changePasswordState.success = true;
          // toast.success(formatMessage(response.data.Message));
        }
      })
      .catch((err: any) => {
        this.changePasswordState.error = err;
        // toast.error(formatMessage("SomethingWentWrong"));
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  UpdatePasswordServiceNew = (data: IChangePassword) => {
    this.changePasswordStateNew.inProgress = true;
    let url: string = URLConstants.ResetPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordStateNew.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.changePasswordStateNew.success = true;
          // toast.success(formatMessage(response.data.Message));
        }
      })
      .catch((err: any) => {
        this.changePasswordStateNew.error = err;
        // toast.error(formatMessage("SomethingWentWrong"));
      })
      .finally(
        action(() => {
          this.changePasswordStateNew.inProgress = false;
        })
      );
  };

  GetUsersByTenantIdService = (
    currentPage: number,
    pagerSize: number,
    tenantId: number,
    filterQuery: string
  ) => {
    this.getUserListServiceState.inProgress = true;
    const url = `${URLConstants.GetUserByTenantId}?TenantId=${tenantId}&PageSize=${pagerSize}&PageNo=${currentPage}&filterQuery=${filterQuery}`;

    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IUserListVM>>) => {
        if (response.data.Error) {
          this.getUserListServiceState.error = response.data.Message;
        } else {
          this.getUserListServiceState.success = true;
          this.userList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getUserListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getUserListServiceState.inProgress = false;
        })
      );
  };

  GetAllUsersService = () => {
    this.getAllUsersServiceState.inProgress = true;
    const url = URLConstants.AllUsers;

    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getAllUsersServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.getAllUsersServiceState.success = true;
          this.userListAll = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getAllUsersServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getAllUsersServiceState.inProgress = false;
        })
      );
  };

  UpdateUserAccountStatusService = (
    UserId: number,
    Email: string,
    AccountStatus: number
  ) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.UpdateUserAccountStatus;
    let userData: any = {
      UserId,
      Email,
      AccountStatus,
    };
    return baseService
      .postRequest(url, userData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allUsers(): IUserList[] {
    let userData: IUserList[] = [];
    const tenantId = getUserIdFromToken();
    if (this.userList?.Users && this.userList.Users?.length > 0) {
      this.userList.Users.map((user) => {
        const {
          Id,
          Email,
          UserName,
          UserType,
          Comment,
          AccountStatus,
          CreatedBy,
          CreatedDate,
          LinkExpirationTime,
          FactoryName,
          CreatedByDesignation,
          CreatedByName,
          UserId,
          IsReserved,
        }: IUserVM = user;
        // let isDelete = true;
        // let isEdit = true;
        let isChangePassword = false;
        let isActivationLink = false;
        // let isResend = true;
        if (getUserType() !== 'FactoryProductionManager')
          userData.push({
            isResend: user.Id !== tenantId && user.AccountStatus !== 'Active',
            isEdit: user.Id == tenantId || user.AccountStatus == 'Active',
            isDelete: user.Id !== tenantId && !IsReserved,
            isChangePassword: isChangePassword,
            isActivationLink: isActivationLink,
            isActive: user.AccountStatus === 'Active',
            UserType: formatMessage(userTypeEnum[user.UserType]),
            CreatedByDesignation: formatMessage(
              userTypeEnum[user.CreatedByDesignation]
            ),
            Id,
            Email,
            UserName,
            AccountStatus,
            Comment: Comment || 'N/A',
            LinkExpirationTime,
            CreatedBy,
            CreatedDate,
            FactoryName,
            CreatedByName,
            UserId,
            IsReserved,
            // isResend: (user.Id == this.tenantAccessId ? false : (user.AccountStatus == "Active") ? false : true),
            // isEdit: (user.Id == this.tenantAccessId ? true : (user.AccountStatus == "Active") ? true : false),
            // isDelete: user.Id == this.tenantAccessId ? false : isDelete,
          });
        // }
        else if (
          UserType === userTypeEnum.FactoryProductionManager ||
          UserType === userTypeEnum.Operator
        )
          userData.push({
            isResend: user.Id !== tenantId && user.AccountStatus !== 'Active',
            isEdit: user.Id == tenantId || user.AccountStatus == 'Active',
            isDelete: user.Id !== tenantId && !IsReserved,
            isChangePassword: isChangePassword,
            isActivationLink: isActivationLink,
            isActive: user.AccountStatus === 'Active',
            UserType: formatMessage(userTypeEnum[user.UserType]),
            CreatedByDesignation: formatMessage(
              userTypeEnum[user.CreatedByDesignation]
            ),
            Id,
            Email,
            UserName,
            AccountStatus,
            Comment: Comment || 'N/A',
            LinkExpirationTime,
            CreatedBy,
            CreatedDate,
            FactoryName,
            CreatedByName,
            UserId,
            IsReserved,
            // isResend: (user.Id == this.tenantAccessId ? false : (user.AccountStatus == "Active") ? false : true),
            // isEdit: (user.Id == this.tenantAccessId ? true : (user.AccountStatus == "Active") ? true : false),
            // isDelete: user.Id == this.tenantAccessId ? false : isDelete,
          });
      });
    }
    return userData;
  }

  get userDetails(): IAddUser {
    if (this.user) {
      return {
        TenantId: this.user.TenantId,
        UserType: this.user.UserType,
        Email: this.user.Email,
        UserName: this.user.UserName,
        ConfirmPassword: this.user.ConfrimPassword || '',
        Password: this.user.Password || '',
        Comment: this.user.Comment,
        AccountStatus:
          this.user.AccountStatus.toLowerCase() === 'active'
            ? AccountStatusEnum.Active
            : this.user.AccountStatus.toLowerCase() === 'pending'
              ? AccountStatusEnum.Pending
              : AccountStatusEnum.Deactivate,
        UserId: this.user.UserID,
        IsReserved: this.user.IsReserved,
        Id: this.user.Id,
        ProcessId: this.user.ProcessId,
      };
    }
    return AddEditUserInitialState;
  }

  get allUserAssignedMulitSelectOptions(): IMultiSelectOption[] {
    let selectedProcesses: IMultiSelectOption[] = [];
    if (this.user && this.user.Process && this.user.Process.length > 0) {
      this.user.Process.map((process) => {
        selectedProcesses.push({
          value: String(process.Id),
          label: process.ProcessName,
        });
      });
    }
    return selectedProcesses;
  }
  //#endregion

  //#region Reset
  resetAddUpdateUserState = () => {
    this.addUpdateUserState.inProgress = false;
    this.addUpdateUserState.success = false;
    this.addUpdateUserState.error = '';
  };

  resetAccountActivationLink = () => {
    this.resendActivationLinkState = { ...this.initialStateValue };
  };

  resetUserDetail = () => {
    this.user = undefined;
    this.userState = { ...this.initialStateValue };
  };

  resetDeleteUser = () => {
    this.deleteUserState = { ...this.initialStateValue };
  };

  resetChangePassword = () => {
    this.changePasswordState = { ...this.initialStateValue };
  };

  resetAccountActivationState = () => {
    this.activateUserAccountState = { ...this.initialStateValue };
  };

  resetGetUserListServiceState = () => {
    this.getUserListServiceState = { ...this.initialStateValue };
  };

  resetActiveInactiveState = () => {
    this.activeInactiveState = { ...this.initialStateValue };
  };

  reset = () => {
    this.addUpdateUserState.success = false;
    this.addUpdateUserState.error = '';
    this.deleteUserState = { ...this.initialStateValue };
    this.changePasswordState = { ...this.initialStateValue };
    this.addUpdateUserState.inProgress = false;
    this.userState = { ...this.initialStateValue };
    this.userList = GetAllUserState;
  };

  resetChangePasswordNew = () => {
    this.changePasswordStateNew = { ...this.initialStateValue };
  };
  //#endregion
}

export default new UserStore();
